import { SelectOption } from 'components/shared/Select'
import { action, computed, observable } from 'mobx'
import { ApiProjectTypes, ApiVovSubtypes, ProjectDTO, ProjectTypes } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { PROJECT_STORE } from 'store/ProjectStore'
import { RootStore } from 'store/RootStore'
// import { posters } from './ProjectPageStore.utils'

export const PROJECT_PAGE_STORE = 'PROJECT_PAGE_STORE'

export class ProjectPageStore {
  private _rootStore: RootStore

  constructor (rootStore: RootStore) {
    this._rootStore = rootStore
  }

  @observable project: ProjectDTO = null
  @observable public projectHasUnsavedChanges: boolean = false

  @action setProject = (project: ProjectDTO) => {
    this.project = project
  }

  @action public setIfProjectHasUnsavedChanges = (value: boolean) => {
    this.projectHasUnsavedChanges = value
  }

  clearStore = () => {
    this.setProject(null)
    this.setIfProjectHasUnsavedChanges(false)
  }

  setProjectProps = (props: Partial<ProjectDTO>) => {
    this.setProject({
      ...this.project || {} as ProjectDTO,
      ...props
    })

    this.setIfProjectHasUnsavedChanges(true)
  }

  startNewProject = () => {
    this.setProject({
      actors: [],
      brands: [],
      children: [],
      createdDate: null,
      directors: [],
      duration: null,
      externalId: null,
      frames: null,
      genres: [],
      guestActors: [],
      height: null,
      id: null,
      images: [],
      info: '',
      loadContents: null,
      loadHierarchy: null,
      mainImage: null,
      marketplaceType:"TECH",
      mergeWithMarketplace: null,
      name: '',
      ownerId: null,
      parentExternalId: null,
      parentId: null,
      producers: [],
      products: [],
      readyForEdit: null,
      relatedMedia: [],
      startTime: null,
      status: null,
      subtype: null,
      tags: '',
      type: null as never,
      urlSpriteG: null,
      urlSpriteL: null,
      urlSpriteS: null,
      videoEntityId: null,
      videoUrl: null,
      width: null,
      year: null,
      number: null,
      autoGroupAccuracy: null
    })
    this.setIfProjectHasUnsavedChanges(false)
  }

  startNewSubProject = () => {
    const {
      parentProjectDetails
    } = this._rootStore[PROJECT_STORE]

    this.setProject({
      actors: parentProjectDetails.actors,
      brands: parentProjectDetails.brands,
      children: null,
      createdDate: null,
      directors: parentProjectDetails.directors,
      duration: null,
      externalId: null,
      frames: null,
      genres: parentProjectDetails.genres,
      guestActors: parentProjectDetails.guestActors,
      height: null,
      id: null,
      images: parentProjectDetails.images,
      info: parentProjectDetails.info,
      loadContents: null,
      loadHierarchy: null,
      mainImage: null,
      marketplaceType:"TECH",
      mergeWithMarketplace: null,
      name: null,
      number: null,
      ownerId: null,
      parentExternalId: null,
      parentId: parentProjectDetails.id,
      producers: parentProjectDetails.producers,
      products: parentProjectDetails.products,
      readyForEdit: null,
      relatedMedia: parentProjectDetails.relatedMedia as any,
      startTime: null,
      status: null,
      subtype: null,
      tags: parentProjectDetails.tags,
      type: (parentProjectDetails.type === ProjectTypes.TV_SERIES
        ? ProjectTypes.TV_SEASON
        : ProjectTypes.TV_EPISODE) as never,
      urlSpriteG: null,
      urlSpriteL: null,
      urlSpriteS: null,
      videoEntityId: null,
      videoUrl: null,
      width: null,
      year: parentProjectDetails.year,
      autoGroupAccuracy: null
    })

    this.setIfProjectHasUnsavedChanges(false)
  }

  setProjectToEdit = () => {
    const { projectToView } = this._rootStore[PROJECT_STORE]
    if (!projectToView || JSON.stringify(projectToView) === '{}') {
      return
    }
    this.setProject(projectToView.asDTO)
    this.setIfProjectHasUnsavedChanges(false)
  }

  createProject = () => {
    this._rootStore[PROJECT_STORE].createProject(this.project)
    this.setIfProjectHasUnsavedChanges(false)
  }

  updateProject = () => {
    const {
      [PROJECT_STORE]: {
        updateProject: update
      },
      [GENERAL_STORE]: {
        push,
        showSuccess
      }
    } = this._rootStore

    update(this.project)
      .then(() => {
        this.setIfProjectHasUnsavedChanges(false)
        push('/projects')
        showSuccess('Saved.')
      })
  }

  onAddSubProjectClick = () => {
    const {
      showInfo,
      push
    } = this._rootStore[GENERAL_STORE]

    if (!this.project || !this.project.id) {
      showInfo('You have to save the current project first.')
      return
    }

    push(`/projects/create/${this.project.id}`)
  }

  triggerVideoProcessing = () => {
    return this._rootStore[PROJECT_STORE].triggerVideoProcessing(this.project)
  }

  @computed get isReadyToSave (): boolean {
    const { name, type } = this.project || {}
    return Boolean((name || '').trim() && type)
  }

  @computed get hasBrands (): boolean {
    if (!this.project || !this.project.brands) return false
    return Boolean(this.project.brands.length)
  }

  @computed get productsCount (): number {
    if (!this.project || !this.project.brands) return 0
    return this.project.brands
      .reduce(
        (count, brand) => count + (brand.products || []).length,
        0
      )
  }

  @computed get projectTypeOptions (): SelectOption<ProjectTypes>[] {
    if (!this.project) return []
    if (this.project.id || this.project.parentId) {
      return [{
        label: this.project.type,
        value: this.project.type
      }]
    }
    if (!this.project.parentId) {
      return Object.values(ApiProjectTypes).map(type => ({
        label: type,
        value: type as ProjectTypes
      }))
    }

    return []
  }

  @computed get projectVovSubtypes (): SelectOption<ApiVovSubtypes>[] {
    return Object.values(ApiVovSubtypes).map(type => ({
      label: type,
      value: type as ApiVovSubtypes
    }))
  }

  @computed get seriesOptions (): SelectOption<number>[] {
    if (
      !this.project ||
      !this.project.parentId ||
      ![ProjectTypes.TV_EPISODE, ProjectTypes.TV_SEASON].includes(this.project.type)
    ) return null

    const {
      parentProjectDetails
    } = this._rootStore[PROJECT_STORE]

    if (parentProjectDetails.type === ProjectTypes.TV_SERIES) {
      return [{
        label: parentProjectDetails.name,
        value: parentProjectDetails.id
      }]
    }

    return null
  }

  @computed get seasonOptions (): SelectOption<number>[] {
    if (
      !this.project ||
      !this.project.parentId ||
      this.project.type !== ProjectTypes.TV_EPISODE
    ) return null

    const {
      parentProjectDetails
    } = this._rootStore[PROJECT_STORE]

    if (parentProjectDetails.type === ProjectTypes.TV_SEASON) {
      return [{
        label: parentProjectDetails.name,
        value: parentProjectDetails.id
      }]
    }

    if (parentProjectDetails.type === ProjectTypes.TV_SERIES) {
      const seasonProject = parentProjectDetails.children
        .find(p => p.id === this.project.parentId)
      return [{
        label: seasonProject.name,
        value: seasonProject.id
      }]
    }

    return null
  }

  @computed get canAttachVideo (): boolean {
    if (!this.project) return false
    return [ProjectTypes.MOVIE, ProjectTypes.VOV, ProjectTypes.TV_EPISODE, ProjectTypes.SPORT]
      .includes(this.project.type)
  }
}
